import { Modal } from '@/components/ui/Modal';
import { Skeleton } from '@/components/ui/Skeleton';
import { usePlan } from '@/hooks/api/useDashboard';
import { isPlanAvailable, type DashboardFilters } from '@/api/dashboard';

import { PlanSections } from './PlanRows';
import styles from './PlanModal.module.css';

interface PlanModalProps {
  open: boolean;
  onClose: () => void;
  /** Home filters (dateRange). The provider NPI is merged in below. */
  filters?: DashboardFilters;
  /** Provider NPI from the signed-in user; required for a real plan. */
  npi: string | null;
}

/**
 * "View My Plan" performance summary (PLA-253, V15 iteration).
 *
 * Opened from the Home hero. Renders the provider's plan from
 * `GET /api/v1/app/plan`: the AI-narrated performance paragraph, then
 * strength and opportunity cards whose notes carry the clinical tooltip
 * copy for the specific indicators behind each number. Everything shown is
 * generated from live gold-layer data; nothing is hard-coded.
 *
 * Deliberately NOT rendered while their features are pending:
 *   - the RSQ/FFS/VBC/IP-A domain mini-grid (only RSQ is measured today;
 *     the narrative names the standing instead)
 *   - the focus-areas progress ring and the "Start in Curate" hand-off
 *     (Curate has not shipped)
 * See App-Mocks/plan-modal-v1.html for the full-featured target layout.
 */
export function PlanModal({ open, onClose, filters, npi }: PlanModalProps) {
  // Thread the provider NPI through; the API needs it to anchor peer rank and
  // the stored-plan key. No NPI -> the query is disabled and we show a hint.
  const planFilters: DashboardFilters = { ...filters, provider: npi ?? undefined };
  const query = usePlan(planFilters, open && !!npi);
  const data = query.data;

  const title = (
    <h2 id="plan-title" className={styles.title}>
      My Performance Summary
    </h2>
  );

  // Period label ("Last 90 Days") comes from the plan payload so the intro
  // always names the window the numbers were computed over. The intro lives
  // in the body (not the Modal subtitle slot) so it doesn't stretch the
  // header bar.
  const periodLabel = data && isPlanAvailable(data) ? data.period.label : null;

  const intro = (
    <p className={styles.intro}>
      A 30-second read on where you stand and where to focus.
      {periodLabel ? ` ${periodLabel}.` : ''}
    </p>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      labelledBy="plan-title"
      fullScreenOnMobile
      wide
      title={title}
    >
      {!npi ? (
        <div className={styles.empty}>
          <strong>Your plan needs a provider NPI.</strong>
          <p>We couldn't read an NPI from your account, so a personalized plan can't be generated yet.</p>
        </div>
      ) : query.isLoading ? (
        <div aria-busy="true" className={styles.loading}>
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="95%" />
          <Skeleton variant="text" width="60%" />
          <ul className={styles.list}>
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className={styles.row}>
                <div className={styles.rowMain}>
                  <Skeleton variant="text" width="30%" />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="45%" />
                </div>
                <div className={styles.rowSide}>
                  <Skeleton variant="text" width={44} height={22} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : query.isError ? (
        <div className={styles.empty}>
          <strong>Couldn't load your plan.</strong>
          <p>{(query.error as Error)?.message ?? 'Kai-API request failed.'}</p>
        </div>
      ) : !data || !isPlanAvailable(data) ? (
        <div className={styles.empty}>
          <strong>Your plan is still processing.</strong>
          <p>{(data && !isPlanAvailable(data) ? data.reason : null) ?? 'Check back once this period’s charts have been analyzed.'}</p>
        </div>
      ) : (
        <div className={styles.content}>
          {intro}
          <PlanSections
            summaryHtml={data.performance.summary}
            strengths={data.strengths}
            opportunities={data.opportunities}
          />
        </div>
      )}
    </Modal>
  );
}
